import React from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';

import { css, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import EmptyState from '@atlaskit/empty-state/empty-state';
import Button from '@atlaskit/button/button';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { expValEquals } from '@confluence/feature-experiments';
import { useSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/useSpaceEnabledContentTypes.ts';
import { useCreateHomeEmptyStateDoc } from '@confluence/create-blank-fabric-page/entry-points/useCreateHomeEmptyStateDoc.ts';
import { usePageSpaceKey } from '@confluence/page-context';

import PopularFeedLight from '../common/images/PopularFeedLight.svg';
import PopularFeedDark from '../common/images/PopularFeedDark.svg';
import PopularFeedSkeletonLight from '../common/images/PopularFeedSkeletonLight.svg';
import PopularFeedSkeletonDark from '../common/images/PopularFeedSkeletonDark.svg';

import { useCreateDoc } from './useCreateDoc.tsx';

const CREATE_DOC_SOURCE = 'popularFeedEmptyState';

const messages = defineMessages({
	heading: {
		id: 'feed.end-of-popular.header',
		defaultMessage: "Nothing's trending just yet",
		description: 'Heading when popular feed has no content',
	},
	mohitiBannerTitle: {
		id: 'feed.end-of-popular.mohiti.banner.title',
		defaultMessage: "Nothing's trending just yet.",
		description:
			'Bold leading text shown in the Mohiti Popular feed empty-state banner before the supporting description.',
	},
	description: {
		id: 'feed.end-of-popular.description',
		defaultMessage:
			'Kick things off by creating something others will want to read, react to, and share.',
		description: 'Description encouraging content creation',
	},
	ctaCreateDoc: {
		id: 'feed.end-of-popular.cta.create-doc',
		defaultMessage: 'Create doc',
		description:
			'The text is shown on a call-to-action button displayed at the end of the Popular feed, prompting the user to create a new live doc (a real-time collaborative document).',
	},
	ctaCreatePage: {
		id: 'feed.end-of-popular.cta.create-page',
		defaultMessage: 'Create page',
		description:
			'The text is shown on a call-to-action button displayed at the end of the Popular feed, prompting the user to create a new Confluence page.',
	},
	emptyStateBannerTitle: {
		id: 'feed.end-of-popular.banner.title',
		defaultMessage: "Nothing's trending just yet.",
		description:
			'Bold leading text shown in the Popular feed empty-state banner before the supporting description.',
	},
});

const endOfFeedImageStyles = css({
	maxWidth: '74px',
	maxHeight: '74px',
});

const dividerContainerStyles = css({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	paddingTop: token('space.200'),
	marginTop: token('space.200'),
});

const skeletonCardImageStyles = css({
	display: 'block',
	height: 'auto',
	width: '100%',
});

const mohitiPrimitiveStyles = cssMap({
	banner: {
		borderRadius: token('radius.xlarge'),
		marginTop: token('space.100'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	content: {
		minWidth: 0,
	},
	text: {
		minWidth: 0,
	},
	// Hardcoded 32px (== token('space.400')); using the token with cssMap trips eslint.
	iconSlot: {
		alignItems: 'center',
		display: 'flex',
		flex: '0 0 auto',
		height: '32px',
		justifyContent: 'center',
		width: '32px',
	},
});

const popularFeedImage = (
	<Image
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
		css={endOfFeedImageStyles}
		src={PopularFeedLight}
		srcDark={PopularFeedDark}
		width="74"
		height="74"
		alt=""
	/>
);

/**
 * Legacy Popular feed empty state (rendered when `cc_mohiti_for_you_empty_states_exp` is off).
 * Unchanged behaviour: label toggles between "Create doc" / "Create page" and the CTA routes
 * through the existing {@link useCreateDoc} flow.
 */
const LegacyEndOfPopularFeed: React.FC<WrappedComponentProps> = ({ intl }) => {
	const { createDoc } = useCreateDoc();
	const [spaceKey] = usePageSpaceKey();
	const { isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);

	const actionButton = (
		<Button onClick={createDoc}>
			<FormattedMessage
				{...(isCreateLiveDocEnabled ? messages.ctaCreateDoc : messages.ctaCreatePage)}
			/>
		</Button>
	);

	return (
		<div css={dividerContainerStyles}>
			<EmptyState
				header={intl.formatMessage(messages.heading)}
				description={<FormattedMessage {...messages.description} />}
				renderImage={() => popularFeedImage}
				primaryAction={actionButton}
			/>
		</div>
	);
};

const MohitiPopularSkeletonCards = () => (
	<Box aria-hidden="true" testId="popular-feed-skeleton-cards">
		<Image
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={skeletonCardImageStyles}
			testId="popular-feed-skeleton-card"
			src={PopularFeedSkeletonLight}
			srcDark={PopularFeedSkeletonDark}
			alt=""
		/>
	</Box>
);

/**
 * Mohiti Popular feed empty state (rendered when `cc_mohiti_for_you_empty_states_exp` is on).
 *
 * Delegates the "Create doc" CTA to the {@link useCreateHomeEmptyStateDoc} helper, whose
 * fallback chain is: personal-space live doc -> CWR -> creation link.
 */
const MohitiEndOfPopularFeed: React.FC = () => {
	const { createDoc } = useCreateHomeEmptyStateDoc({ source: CREATE_DOC_SOURCE });

	return (
		<Stack space="space.300">
			<Box backgroundColor="elevation.surface.sunken" xcss={mohitiPrimitiveStyles.banner}>
				<Inline alignBlock="center" spread="space-between" space="space.200">
					<Inline
						alignBlock="center"
						grow="fill"
						space="space.200"
						xcss={mohitiPrimitiveStyles.content}
					>
						<Box
							xcss={mohitiPrimitiveStyles.iconSlot}
							testId="popular-feed-empty-icon"
							aria-hidden="true"
						>
							<Image
								src={PopularFeedLight}
								srcDark={PopularFeedDark}
								width="32"
								height="32"
								alt=""
							/>
						</Box>
						<Box xcss={mohitiPrimitiveStyles.text}>
							<Text as="p">
								<Text as="span" weight="bold">
									<FormattedMessage {...messages.mohitiBannerTitle} />
								</Text>{' '}
								<FormattedMessage {...messages.description} />
							</Text>
						</Box>
					</Inline>
					<Button onClick={createDoc}>
						<FormattedMessage {...messages.ctaCreateDoc} />
					</Button>
				</Inline>
			</Box>
			<MohitiPopularSkeletonCards />
		</Stack>
	);
};

const EndOfPopularFeedComponent: React.FC<WrappedComponentProps> = ({ intl }) => {
	if (expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true)) {
		return <MohitiEndOfPopularFeed />;
	}

	return <LegacyEndOfPopularFeed intl={intl} />;
};

export const EndOfPopularFeed: React.FC<WithIntlProps<WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<WrappedComponentProps>;
} = injectIntl(EndOfPopularFeedComponent);
